import { useState, useEffect, useCallback, PropsWithChildren, useRef } from 'react';
import styles from './field.module.css';
import { Button } from '../form/button';
import Icon from '../icons/icon';
import { Typography, TypographyTypes, TypographyWeight } from '../typography/typography';
import classNames from 'classnames';
import { animated, config, useTransition } from 'react-spring';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import '../dropdown/dropdown.scss';
import { IconNames } from '../icons/icons';
import { isArray, isNaN } from 'lodash';
import { SelectForm } from '../select-form/select-form';
import { Props } from 'react-select';
import { Spinner } from '../spinner/spinner';

export type FieldLabelProps = {
  label?: string;
  labelType?: keyof typeof TypographyTypes;
  labelWeight?: keyof typeof TypographyWeight;
  isRequired?: boolean;
  dataCy?: string;
};

export type FieldDescriptionProps = {
  description?: string;
  dataCy?: string;
};

export type FieldProps = FieldLabelProps &
  FieldDescriptionProps & {
    id?: string;
    className?: string;
    name?: string;
    type: 'text' | 'number' | 'password' | 'currency' | 'dropdown';
    value: any;
    onChange: (newValue: any) => any;
    disabled?: boolean;
    maximumFractionDigits?: number;
    placeholder?: string;
    dataCy?: string;
    invalid?: boolean;
    options?: { label: string; value: string }[];
    format?: boolean;
    isClearable?: boolean;
    autoComplete?: string;
    dropdownProps?: Props;
    autofocus?: boolean;
  };

type EditableFieldProps = Omit<FieldProps, 'type'> & {
  type: 'text' | 'number' | 'dropdown';
  formattedValue?: string;
  noValueText?: string;
  placement?: Placement;
  weight?: keyof typeof TypographyWeight;
  canReset?: boolean;
  resetPosition?: 'left' | 'right';
  onOpen?: () => void;
  onClose?: () => void;
  onReset?: () => void;
  dataCy?: string;
};

type FileFieldProps = FieldLabelProps &
  FieldDescriptionProps & {
    additionalText?: string;
    icon?: IconNames;
    showIcon?: boolean;
    id?: string;
    className?: string;
    accept?: string;
    value: File | File[] | undefined;
    dataCy?: string;
    onChange: (newFile?: File | File[]) => any;
    multiple?: boolean;
    customPreview?: boolean;
    showClickToUploadText?: boolean;
    isSaving?: boolean;
  };

export const FieldLabel = ({
  label,
  labelType = 'text-sm',
  labelWeight = 'medium',
  isRequired = false,
}: FieldLabelProps) => {
  return label ? (
    <div className="mb-[6px]">
      <Typography type={labelType} weight={labelWeight} className="text-grey-700">
        {label} {isRequired ? <span className={styles.requiredField}>*</span> : ''}
      </Typography>
    </div>
  ) : null;
};

export const FieldDescription = ({ description }: { description?: string }) => {
  return description ? (
    <div className="mt-[6px]">
      <Typography type="text-sm" className="text-grey-500">
        {description}
      </Typography>
    </div>
  ) : null;
};

export const Field = ({
  id,
  className,
  label,
  type,
  value,
  onChange,
  labelType,
  labelWeight,
  isRequired,
  isClearable,
  description,
  disabled = false,
  maximumFractionDigits,
  placeholder,
  dataCy,
  invalid = false,
  options,
  format = true,
  name,
  autoComplete,
  dropdownProps,
  autofocus,
}: FieldProps) => {
  const [numberValue, setNumberValue] = useState<{ displayValue: string; value?: number }>({ displayValue: '' });
  useEffect(() => {
    if (value && ['number', 'currency'].includes(type) && value !== numberValue.value) {
      onNumberChange(value.toString());
    } else if (['number', 'currency'].includes(type) && value !== numberValue.value) {
      setNumberValue({ displayValue: '', value: undefined });
    }
  }, [value, type]);

  const onNumberChange = (input: string) => {
    const numberParts = input.split('.', 2);
    const intPart = numberParts[0].replace(/[^\-?|0-9]/g, '');
    const fractionPart = (numberParts[1] || '').replace(/[^0-9]/g, '').slice(0, maximumFractionDigits);

    const newNumber = `${intPart}${fractionPart ? `.${fractionPart}` : ''}`;
    const newNumberIntPart = format ? `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : intPart;
    const newNumberFormatted = `${newNumberIntPart}${
      numberParts.length === 2 && maximumFractionDigits !== 0 ? '.' : ''
    }${fractionPart}`;
    const newNumberValue = newNumber === '' ? undefined : +newNumber;

    if (numberValue.value !== newNumberValue) {
      setNumberValue({ displayValue: newNumberFormatted, value: newNumberValue });
      return onChange(newNumberValue);
    } else return setNumberValue({ ...numberValue, displayValue: newNumberFormatted });
  };

  return (
    <div className={classNames(className)}>
      <FieldLabel label={label} labelType={labelType} labelWeight={labelWeight} isRequired={isRequired} />

      {type === 'dropdown' ? (
        <SelectForm
          isMulti={false}
          options={options}
          isClearable={isClearable}
          menuShouldBlockScroll
          {...dropdownProps}
          menuPlacement="bottom"
          placeholder={'Select option'}
          onChange={onChange}
          value={value}
          autoFocus={autofocus}
        />
      ) : type === 'number' ? (
        <div
          className={`m-0 box-border w-full rounded-lg border bg-white font-sans text-gray-700 ${
            invalid ? 'border-[#fda29b]' : 'border-gray-300'
          }`}
        >
          <input
            id={id}
            name={name}
            className={classNames(
              `m-0 box-border w-full rounded-lg border border-solid bg-white pl-1 pr-1 font-sans text-gray-700`,
              styles.input,
            )}
            data-cy={dataCy}
            type="text"
            value={numberValue.displayValue}
            onChange={(ce) => onNumberChange(ce.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoFocus={autofocus}
          />

          <FieldDescription description={description} />
        </div>
      ) : type === 'currency' ? (
        <div
          className={`border border-solid ${
            invalid ? 'border-[#fda29b]' : 'border-[var(--grey-300)]'
          } rounded-lg flex-[0_1_100%] flex items-center`}
        >
          <span className="pl-[0.8rem] quipli-v2">$</span>
          <input
            id={id}
            className={classNames(
              'appearance-textfield !outline-none !border-0 !border-none quipli-v2 flex-[0_1_95%]',
              styles.input,
            )}
            data-cy={dataCy}
            type="text"
            value={numberValue.displayValue}
            onChange={(ce) => onNumberChange(ce.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoFocus={autofocus}
          />

          <FieldDescription description={description} />
        </div>
      ) : (
        <div
          className={`m-0 box-border w-full rounded-lg border bg-white font-sans text-gray-700 ${
            invalid ? 'border-[#fda29b]' : 'border-gray-300'
          }`}
        >
          <input
            id={id}
            className={classNames(
              `m-0 box-border w-full rounded-lg border border-solid bg-white pl-1 pr-1 font-sans text-gray-700`,
              styles.input,
            )}
            data-cy={dataCy}
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(ce) => onChange(ce.target.value)}
            disabled={disabled}
            autoComplete={autoComplete}
            autoFocus={autofocus}
          />

          <FieldDescription description={description} />
        </div>
      )}
    </div>
  );
};

export const FieldCounter = ({
  value = 1,
  min = 1,
  max,
  onChange,
  disabled = false,
  name,
  className,
  allowDecimals = false,
  hideButtons = false,
  dataCy,
}: {
  value?: number;
  min?: number;
  max?: number;
  onChange: (newValue: number) => void;
  disabled?: boolean;
  name?: string;
  className?: string;
  allowDecimals?: boolean;
  hideButtons?: boolean;
  dataCy?: string;
}) => {
  const [inputValue, setInputValue] = useState<string>(value.toString());

  const handleIncrement = () => {
    if (max && value >= max) return;
    onChange(+value + 1);
  };

  const handleDecrement = () => {
    if (value <= min) return;
    onChange(+value - 1);
  };

  const handleOnChange = (newValue: string) => {
    let parsedValue = +newValue;
    if (!allowDecimals) parsedValue = parseInt(newValue);

    if (parsedValue < min || isNaN(parsedValue)) {
      onChange(min);
      setInputValue(min.toString());
      return;
    }

    if (parsedValue >= min && (!max || parsedValue <= max)) {
      onChange(parsedValue);
      setInputValue(parsedValue.toString());
    }
  };

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  return (
    <div
      className={classNames(
        'p-[6px] flex justify-center items-center gap-x-[3px] border border-solid border-grey-300 rounded-[9px] w-fit',
        className,
        { 'bg-grey-50': disabled },
      )}
      data-cy={dataCy}
    >
      <Button
        className={classNames('button-tertiary !p-0', {
          '!hidden': hideButtons,
        })}
        onClick={handleDecrement}
        disabled={disabled}
        dataCy="field-counter-decrement"
      >
        <Icon icon="Minus" stroke={disabled ? 'var(--grey-400)' : undefined} />
      </Button>

      <div className={styles.counter}>
        <input
          data-cy="field-counter-input"
          type="number"
          name={name}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={(e) => handleOnChange(e.target.value)}
          disabled={disabled}
        />
      </div>

      <Button
        className={classNames('button-tertiary !p-0', {
          '!hidden': hideButtons,
        })}
        onClick={handleIncrement}
        disabled={disabled}
        dataCy="field-counter-increment"
      >
        <Icon icon="Plus" stroke={disabled ? 'var(--grey-400)' : undefined} />
      </Button>
    </div>
  );
};

export const EditableField = ({
  placement = 'bottom-start',
  onClose = () => {},
  onOpen = () => {},
  onReset = () => {},
  canReset = false,
  resetPosition = 'left',
  weight = 'regular',
  formattedValue,
  noValueText,
  value,
  onChange,
  ...props
}: PropsWithChildren<EditableFieldProps>) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, { placement, strategy: 'fixed' });

  const transitions = useTransition(isOpen, {
    from: {
      opacity: 0,
      transform: `scale(${0.9})`,
      transformOrigin: 'top',
    },
    enter: { opacity: 1, display: 'flex', transform: `scale(${1})` },
    leave: { opacity: 0, display: 'none', transform: `scale(${0.9})` },
    config: config.gentle,
  });

  const onCloseDropdown = useCallback((e: Event) => {
    e.stopPropagation();
    onClose();
    setIsOpen(false);
  }, []);

  const closeDropdownEvent = useCallback(
    (e: Event) => {
      if (referenceElement && e.target && referenceElement.contains(e.target as Node)) {
        document.body.addEventListener('click', closeDropdownEvent, { once: true, capture: true });
        return;
      } else {
        document.body.dispatchEvent(new CustomEvent('close-dropdown'));
      }
    },
    [referenceElement],
  );

  useEffect(() => {
    if (isOpen) {
      onOpen();
      document.body.addEventListener('click', closeDropdownEvent, { once: true, capture: true });
      document.body.addEventListener('close-dropdown', onCloseDropdown, { once: true, capture: true });
    }
  }, [isOpen]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <>
      <div
        ref={setReferenceElement}
        className={classNames('flex gap-[4px] items-center', {
          'flex-row-reverse justify-end': resetPosition === 'right',
        })}
      >
        {canReset && (
          <div className="cursor-pointer flex items-center" onClick={() => onReset && onReset()}>
            <Icon icon="Refresh" size="medium" stroke="var(--grey-400)" />
          </div>
        )}

        <div
          className={classNames({ 'cursor-pointer': !props.disabled, 'cursor-not-allowed': props.disabled })}
          onClick={() => {
            !props.disabled && !isOpen && setIsOpen(true);
          }}
        >
          <Typography
            type="text-sm"
            className={classNames('text-link-100', {
              'underline decoration-dashed': canReset,
            })}
            weight={weight}
          >
            <span>{formattedValue || value ? formattedValue || value : noValueText}</span>
          </Typography>
        </div>

        {transitions(
          (transitionStyle, item) =>
            item && (
              <div
                ref={setPopperElement}
                className="flex flex-col items-center justify-center gap-0"
                style={{ zIndex: 2001, ...styles.popper }}
                {...attributes.popper}
              >
                <animated.div
                  className={classNames('quipli-v2 dropdown-content no-background')}
                  style={transitionStyle}
                >
                  <div className="p-[8px] max-w-xs">
                    <div className="flex items-end gap-2">
                      <Field
                        {...props}
                        value={inputValue}
                        onChange={(newInputValue) => setInputValue(newInputValue)}
                        dataCy="popup-field-input"
                      />

                      <Button
                        className="button-primary circle sm mb-1"
                        onClick={(e: any) => {
                          onChange(inputValue);
                          onCloseDropdown(e);
                        }}
                        dataCy="popup-field-save"
                      >
                        <Icon icon="Check" stroke="var(--quipli-white)" />
                      </Button>
                    </div>
                  </div>
                </animated.div>
              </div>
            ),
        )}
      </div>
    </>
  );
};

export const FileField = ({
  label,
  labelType,
  labelWeight,
  isRequired,
  description,
  additionalText,
  value,
  onChange,
  id = 'input-file-form',
  className,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
  showIcon = true,
  icon = 'UploadCloud2',
  multiple = false,
  customPreview = false,
  showClickToUploadText = true,
  isSaving = false,
}: FileFieldProps) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className={classNames(className)}>
        <FieldLabel label={label} labelType={labelType} labelWeight={labelWeight} isRequired={isRequired} />

        {value !== undefined && !customPreview ? (
          <div>
            <div className="flex items-center justify-between gap-x-[4px]">
              <Typography type="text-sm" className="text-grey-400 text-overflow">
                {isArray(value) ? value.map((v) => v.name).join(', ') : value.name}
              </Typography>
              <div className="cursor-pointer" onClick={() => onChange(undefined)}>
                <Icon icon="Trash" stroke="var(--error-400)" />
              </div>
            </div>
          </div>
        ) : (
          <label htmlFor={id}>
            <div ref={ref} className="relative hover:opacity-60 [&.dragover]:opacity-60">
              <div className="file-input-wrapper p-[24px] box-border w-full rounded-[9px] border border-solid border-grey-200 bg-quipli-white flex flex-col gap-y-[12px] min-h-[100px] items-center justify-center ">
                {isSaving ? (
                  <Spinner />
                ) : (
                  <>
                    {showIcon && <Icon icon={icon} />}
                    <div>
                      {showClickToUploadText && (
                        <Typography type="text-sm" className="text-grey-500 text-center">
                          <span className="font-semibold !text-quipli-black-800">Click to upload</span> or drag and drop
                        </Typography>
                      )}

                      {!!additionalText && (
                        <Typography type="text-sm" className="text-grey-500">
                          {additionalText}
                        </Typography>
                      )}
                    </div>
                  </>
                )}
              </div>
              <input
                className="opacity-0 absolute top-0 left-0 w-full h-full cursor-pointer"
                type="file"
                id={id}
                accept={accept}
                multiple={multiple}
                onChange={({ target }) => {
                  if (!target.files) return;

                  if (multiple) {
                    onChange(Array.from(target.files));
                  } else if (target.files[0]) {
                    onChange(target.files[0]);
                  }
                }}
                disabled={isSaving}
                onDragOver={() => ref.current?.classList.add('dragover')}
                onDragLeave={() => ref.current?.classList.remove('dragover')}
              />
            </div>

            <FieldDescription description={description} />
          </label>
        )}
      </div>
    </>
  );
};
